<template>
  <div>
    <label v-if="label" class="form-label" :class="{ required }">{{ label }}</label>
    <div v-if="hint" class="hint-text">{{ hint }}</div>
    <div v-if="cropHint" class="hint-text">{{ cropHint }}</div>

    <AdImagePicker :imageUrl="imageUrl" :uploading="isUploading" boxSize @click="triggerFileInput" @remove="clear" />

    <FormErrorList :errors="errors" />

    <input ref="fileInput" type="file" accept=".jpg,.jpeg,.png,.gif,.webp" class="d-none" @change="onFileSelected" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { FormErrorList } from '@prospective/lithium';
import AdImagePicker from '@/components/form/AdImagePicker.vue';
import { mediaImageUrl, uploadImage } from '@/api/media';
import translator from '@/locale/translator';

/**
 * The artwork for a manual booking's `image` field.
 *
 * The stored value is a bare `images` row ID, not an image object — that is what PJAS validates and
 * what the partner feed expands at delivery time. So this uploads, keeps only the new row's id, and
 * renders it back through the media jump URL.
 */
const modelValue = defineModel<number | string | null>({ default: null });

const props = withDefaults(
  defineProps<{
    label?: string;
    hint?: string;
    errors?: string[];
    required?: boolean;
    cutWidth?: number | null;
    cutHeight?: number | null;
    maxFileSizeKb?: number | null;
  }>(),
  { errors: () => [], required: false }
);

const fileInput = ref<HTMLInputElement | null>(null);
const isUploading = ref(false);

/** The crop and size the type asks for, so it is visible before an upload rather than after. */
const cropHint = computed(() => {
  const parts: string[] = [];

  if (props.cutWidth && props.cutHeight) {
    parts.push(`${props.cutWidth} × ${props.cutHeight} px`);
  }

  if (props.maxFileSizeKb) {
    parts.push(translator.get('campaigns.manual.max_file_size', { size: props.maxFileSizeKb }));
  }

  return parts.join(' · ');
});

const imageUrl = computed(() => mediaImageUrl(modelValue.value));

function triggerFileInput() {
  if (!isUploading.value) fileInput.value?.click();
}

function clear() {
  modelValue.value = null;
}

async function onFileSelected(event: Event) {
  const target = event.target as HTMLInputElement;
  if (!target.files?.length) return;

  isUploading.value = true;
  try {
    const res = await uploadImage(target.files[0]);
    // only the id: the field stores a reference, not a copy of the image object
    const id = Number((res.data as unknown as { id?: number })?.id ?? 0);
    if (id) modelValue.value = id;
  } catch (error) {
    console.error('Failed to upload image:', error);
  } finally {
    isUploading.value = false;
    if (fileInput.value) fileInput.value.value = '';
  }
}
</script>

<style scoped lang="scss">
.form-label {
  display: block;
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
  margin-bottom: 6px;
  color: #344054;

  &.required::after {
    content: '*';
    color: #ef4444;
    margin-left: 2px;
  }
}

.hint-text {
  font-size: 12px;
  line-height: 18px;
  color: #475467;
  margin-bottom: 6px;
}
</style>
